import { useEffect } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import AllRoutes from 'routes/index';
import authService from 'services/authService';
import { InitStore, useInitStore } from 'stores/useInitStore';
import { API_KEYS } from 'constants/constants';
import GlobalProvider from 'context/GlobalContext';
import BackendServicesInfo from 'components/BackendServicesInfo';
import { AxiosError } from 'interfaces/interfaces';
import { NotificationsStore, useNotificationsStore } from 'stores/useNotificationsStore';
import { UserStore, useUserStore } from 'stores/useUserStore';

function App() {
  const urlParams = new URLSearchParams(window.location.search);
  const { setAppLoaded, setHostFound, setInitData, initData, setClientGroupsData, setAppLoading } = useInitStore(
    (state: InitStore) => ({
      setAppLoaded: state.setAppLoaded,
      setHostFound: state.setHostFound,
      setInitData: state.setInitData,
      initData: state.initData,
      setClientGroupsData: state.setClientGroupsData,
      setAppLoading: state.setAppLoading,
    }),
  );

  const { user, setUser } = useUserStore((state: UserStore) => ({
    user: state.user,
    setUser: state.setUser,
  }));

  const { processMessages } = useNotificationsStore((state: NotificationsStore) => ({
    processMessages: state.processMessages,
  }));

  useEffect(() => {
    const getInitData = async () => {
      const hostName = document.location.hostname;
      try {
        const response = await authService.getInitData(hostName);
        if (response.status === 200) {
          setAppLoaded(true);
          setHostFound(true);
          setInitData(response.data);
        }
      } catch (error) {
        console.error(error);
        const axiosError = error as AxiosError;
        processMessages(axiosError?.response?.data?.messages);
      }
    };
    getInitData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAppLoaded, setHostFound, setInitData]);

  useEffect(() => {
    if (initData?.isClientGroup && initData?.clientGroupName) {
      const getClientGroup = async () => {
        try {
          const response = await authService.getClientGroup(initData?.clientGroupName);
          if (response.status === 200) {
            setClientGroupsData(response.data);
          }
        } catch (error) {
          console.error(error);
          const axiosError = error as AxiosError;
          processMessages(axiosError?.response?.data?.messages);
        }
      };
      getClientGroup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData?.clientGroupName, initData?.isClientGroup, setClientGroupsData]);

  useEffect(() => {
    if (urlParams.get('clearSession')) {
      setUser(null);
      localStorage.removeItem('selectedUnionHostname');
      window.location.href = window.location.href.split('?')[0];
      setAppLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUser]);

  useEffect(() => {
    if (
      initData?.isClientGroup &&
      user &&
      localStorage.getItem('selectedUnionHostname') &&
      !urlParams.get('clearSession')
    ) {
      window.open(
        `https://${localStorage.getItem('selectedUnionHostname')}/logging-in/${btoa(JSON.stringify(user))}`,
        '_self',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData?.isClientGroup, user]);

  useEffect(() => {
    if (initData?.clientGroupHostName && !user && !location.pathname.includes('/reset-password')) {
      window.open(`https://${initData?.clientGroupHostName}?clearSession=true`, '_self');
    }
  }, [initData?.clientGroupHostName, user]);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={API_KEYS.RECAPTCHA_SITE_KEY}>
      <GlobalProvider>
        <BackendServicesInfo />
        <AllRoutes />
      </GlobalProvider>
    </GoogleReCaptchaProvider>
  );
}

export default App;
